import { createApi } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import getTenantId from "../../components/Tenent";
import { showAccessDeniedModal } from "../slices/authSlice";

import Cookies from "js-cookie";

const getStore = () => require("../store").store;

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_AI_AGENT,
  withCredentials: true,
  headers: {
    accept: "application/json",
  },
});

const setUpInterceptors = (getState, dispatch) => {
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = Cookies.get("access_token");
      const refresh_token = Cookies.get("refresh_token");

      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
        config.headers["Refresh"] = `Bearer ${refresh_token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  axiosInstance.interceptors.response.use(
    (res) => res,
    async (err) => {
      const originalConfig = err.config;
      const errorMessage = err.response?.data?.error || "";

      if (
        err.response?.status === 401 &&
        errorMessage.includes("access token has expired") &&
        !originalConfig._retry
      ) {
        originalConfig._retry = true;
        const accessToken = Cookies.get("access_token");
        const refreshToken = Cookies.get("refresh_token");
        if (!refreshToken) {
          Cookies.remove("access_token");
          Cookies.remove("refresh_token");
          return Promise.reject(err);
        }

        try {
          // Determine the correct tenant ID
          const tenantId = getTenantId();

          const refreshHeaders = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${refreshToken}`,
            "x-tenant-id": tenantId,
          };
          // Request new tokens from `/core/refresh`
          const { data } = await axios.post(
            `${process.env.REACT_APP_AUTH_API}/core/refresh`,
            {
              access_token: accessToken,
              refresh_token: refreshToken,
            },
            { headers: refreshHeaders },
          );

          // Save new tokens in Cookies
          Cookies.set("access_token", data?.access_token, {
            secure: true,
          });
          Cookies.set("refresh_token", data?.refresh_token, {
            secure: true,
          });

          // Retry the original request with the new token
          originalConfig.headers["Authorization"] =
            `Bearer ${data?.access_token}`;
          originalConfig.headers["Refresh"] = `Bearer ${data?.refresh_token}`;
          return axiosInstance(originalConfig);
        } catch (_error) {
          // Refresh failed, clear cookies and redirect to login
          Cookies.remove("access_token");
          Cookies.remove("refresh_token");
          return Promise.reject(_error);
        }
      }

      return Promise.reject(err);
    },
  );
};

const axiosBaseQuery =
  () =>
  async (
    { url, method, body, params, ...requestOpts },
    { getState, dispatch },
  ) => {
    setUpInterceptors(getState, dispatch);

    const tenantId = getTenantId();

    const enhancedHeaders = {
      ...requestOpts.headers,
      "X-Tenant-Id": tenantId,
    };

    try {
      const axiosOptions = {
        url: url,
        method,
        data: body,
        params,
        headers: enhancedHeaders,
      };

      const result = await axiosInstance(axiosOptions);
      return { data: result.data };
    } catch (axiosError) {
      let err = axiosError;
      const errorData = axiosError.response?.data;
      const status = axiosError.response?.status;

      if (status === 403 && errorData?.error === "forbidden") {
        getStore().dispatch(showAccessDeniedModal());
      }
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const initiativeAI = createApi({
  reducerPath: "initiativeAI",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["initiativeAI"],
  endpoints: (builder) => ({
    getSessions: builder.query({
      query: () => ({
        url: `/api/v1/chat/sessions`,
        method: "GET",
      }),
      providesTags: ["session"],
    }),

    getHistory: builder.query({
      query: (id) => ({
        url: `/api/v1/chat/history/${id}`,
        method: "GET",
      }),
    }),

    prompt: builder.mutation({
      query: ({ body }) => ({
        url: "/api/v1/chat",
        body,
        method: "POST",
      }),
    }),

    validateFields: builder.mutation({
      query: (body) => ({
        url: `/api/v1/validate_field`,
        body,
        method: "POST",
      }),
    }),

    submitForm: builder.mutation({
      query: (body) => ({
        url: `/api/v1/chat/submit`,
        body,
        method: "POST",
      }),
    }),

    deleteSession: builder.mutation({
      query: (id) => {
        return {
          url: `/api/v1/chat/sessions/${id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["session"],
    }),
    stream: builder.query({
      query: (id) => {
        return {
          url: `/api/v1/chat/stream/${id}`,
        };
      },
    }),
  }),
});

export const {
  useGetSessionsQuery,
  useLazyGetSessionsQuery,
  useGetHistoryQuery,
  useLazyGetHistoryQuery,
  usePromptMutation,
  useValidateFieldsMutation,
  useSubmitFormMutation,
  useDeleteSessionMutation,
  useLazyStreamQuery,
  useStreamQuery,
} = initiativeAI;

// SSE stream utility — RTK Query can't handle server-sent events natively
export const streamChat = async (sessionId, onStep, signal) => {
  const baseURL = process.env.REACT_APP_AI_AGENT || "";
  const url = `${baseURL}api/v1/chat/stream/${sessionId}`;

  try {
    const tenantId = getTenantId();
    const token = Cookies.get("access_token");
    const refreshToken = Cookies.get("refresh_token");

    const response = await fetch(url, {
      headers: {
        Accept: "text/event-stream",
        "x-tenant-id": tenantId,
        Authorization: token ? `Bearer ${token}` : "",
        Refresh: refreshToken ? `Bearer ${refreshToken}` : "",
      },
      signal,
    });

    if (!response.ok || !response.body) return;

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = lines.pop();

      for (const line of lines) {
        if (!line.startsWith("data:")) continue;

        const raw = line.slice(5).trim();
        if (!raw || raw === "[DONE]") continue;

        try {
          onStep(JSON.parse(raw));
        } catch {
          onStep({ message: raw });
        }
      }
    }
  } catch (err) {
    if (err.name !== "AbortError") {
      console.error("Stream error:", err);
    }
  }
};
